import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, api } from "../App";
import { toast } from "sonner";
import { ChevronRight, ChevronLeft, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

const MASCOT_IMAGE = "https://customer-assets.emergentagent.com/job_a00bdaaa-d343-44f1-adb9-f8b11a8d38bf/artifacts/m964dse2_9C7E8ACF-CDB9-4A1C-A556-DF501523912B.jpeg";

const QUESTIONS = [
  {
    id: "motivation",
    question: "What brought you here?",
    options: [
      { value: "falling_behind", label: "I feel like I am falling behind financially" },
      { value: "referral", label: "Someone told me about Bitcoin" },
      { value: "understand", label: "I want to understand the noise" },
      { value: "believer", label: "I already believe and want to learn more" }
    ]
  },
  {
    id: "goal",
    question: "What is your main goal?",
    options: [
      { value: "understand", label: "Understand what Bitcoin actually is" },
      { value: "buy_store", label: "Learn how to buy and store it safely" },
      { value: "go_deep", label: "Go deep on how it works" },
      { value: "teach_kids", label: "Teach my kids about sound money" }
    ]
  }
];

export default function Onboarding() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    experience_level: "",
    motivation: "",
    goal: ""
  });
  const [loading, setLoading] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState("");

  const totalSteps = 4;
  const progress = ((step + 1) / totalSteps) * 100;

  const canProceed = () => {
    if (step === 1) return answers.motivation !== "";
    if (step === 2) return answers.goal !== "";
    return true;
  };

  const handleNext = async () => {
    if (step < 2) {
      setStep(step + 1);
    } else if (step === 2) {
      setLoading(true);
      try {
        const response = await api.put("/users/onboarding", answers);
        setWelcomeMessage(response.data.welcome);
        updateUser({ onboarding_complete: true });
        setStep(3);
      } catch (error) {
        toast.error("Failed to complete onboarding");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleStartLearning = () => {
    navigate("/course/section/1");
    toast.success("Your journey begins! Welcome to Bitcoin Brain.");
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-zinc-500 text-sm">Step {step + 1} of {totalSteps}</span>
            <span className="text-[#F7931A] text-sm font-medium">{Math.round(progress)}%</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div
              key="welcome"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="text-center"
            >
              <motion.img
                src={MASCOT_IMAGE}
                alt="Bitcoin Brain Mascot"
                className="w-40 h-40 mx-auto rounded-2xl mb-8 animate-float btc-glow"
              />
              <h1 className="font-heading text-4xl font-bold text-white mb-4">
                WELCOME TO <span className="text-[#F7931A]">BITCOIN BRAIN</span>
              </h1>
              <p className="text-zinc-400 text-lg mb-2">
                Hi {user?.first_name || "there"}! Let's personalize your learning journey.
              </p>
              <p className="text-zinc-500 mb-8">
                Two quick questions to customize your experience.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => {
                    setAnswers(prev => ({ ...prev, experience_level: "beginner" }));
                    setStep(1);
                  }}
                  className="btn-primary px-8"
                  data-testid="onboarding-new-btn"
                >
                  I'm new to Bitcoin
                </Button>
                <Button
                  onClick={() => {
                    setAnswers(prev => ({ ...prev, experience_level: "owner" }));
                    setStep(1);
                  }}
                  className="btn-secondary px-8"
                  data-testid="onboarding-know-btn"
                >
                  I already know some Bitcoin
                </Button>
              </div>
            </motion.div>
          )}

          {(step === 1 || step === 2) && (
            <motion.div
              key={`question-${step}`}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="bg-[#0A0A0A] rounded-2xl p-8 border border-white/10"
            >
              <img src={MASCOT_IMAGE} alt="Bitcoin Brain" className="w-20 h-20 rounded-xl mb-6" />
              <h2 className="font-heading text-2xl font-bold text-white mb-6">
                {QUESTIONS[step - 1].question}
              </h2>
              
              <div className="space-y-3">
                {QUESTIONS[step - 1].options.map((option, i) => (
                  <motion.button
                    key={option.value}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    onClick={() => setAnswers(prev => ({ ...prev, [QUESTIONS[step - 1].id]: option.value }))}
                    className={`w-full p-4 rounded-lg border text-left transition-all ${
                      answers[QUESTIONS[step - 1].id] === option.value
                        ? "border-[#F7931A] bg-[#F7931A]/10 text-white"
                        : "border-white/10 bg-[#161616] text-zinc-300 hover:border-[#F7931A]/50"
                    }`}
                    data-testid={`onboarding-option-${option.value}`}
                  >
                    <span className="flex items-center gap-3">
                      <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        answers[QUESTIONS[step - 1].id] === option.value
                          ? "border-[#F7931A] bg-[#F7931A]" : "border-zinc-600"
                      }`}>
                        {answers[QUESTIONS[step - 1].id] === option.value && (
                          <span className="w-2 h-2 rounded-full bg-black" />
                        )}
                      </span>
                      {option.label}
                    </span>
                  </motion.button>
                ))}
              </div>

              <div className="flex justify-between mt-8">
                <Button
                  onClick={() => setStep(step - 1)}
                  variant="ghost"
                  className="text-zinc-400"
                  data-testid="onboarding-back-btn"
                >
                  <ChevronLeft size={20} /> Back
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={!canProceed() || loading}
                  className="btn-primary"
                  data-testid="onboarding-next-btn"
                >
                  {loading ? "Setting up..." : step === 2 ? "Complete" : "Next"}
                  {!loading && <ChevronRight size={20} />}
                </Button>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="complete"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <motion.img
                src={MASCOT_IMAGE}
                alt="Bitcoin Brain"
                className="w-48 h-48 mx-auto rounded-2xl mb-8 btc-glow"
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
              />
              <h2 className="font-heading text-3xl font-bold text-white mb-4">
                YOU'RE ALL SET!
              </h2>
              
              {welcomeMessage && (
                <div className="bg-[#0A0A0A] rounded-xl p-6 border border-[#F7931A]/30 mb-8 text-left">
                  <p className="text-zinc-300 leading-relaxed italic">"{welcomeMessage}"</p>
                  <p className="text-[#F7931A] text-sm mt-3">— Bitcoin Brain</p>
                </div>
              )}
              
              <Button
                onClick={handleStartLearning}
                className="btn-primary text-lg px-8"
                data-testid="start-learning-btn"
              >
                Start Learning <ArrowRight className="ml-2" size={20} />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
